/**
 * Admin Messages Data Hooks
 *
 * React Query hooks for the admin "Messages" tab: the paged, filtered list of
 * outbound messages (keyed by the whole filter object, previous page kept
 * visible while the next one loads), the full detail of one message and the
 * cancel mutation. Cancelling invalidates the list + detail, and the
 * Automations list too (its per-row message counts change).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminMessageCancel, adminMessageGet, adminMessagesList } from "../utils/adminApi";
import { ADMIN_AUTOMATIONS_LIST_QUERY_KEY } from "./useAdminAutomations";

export const ADMIN_MESSAGES_QUERY_KEY = ["admin", "messages"];
export const ADMIN_MESSAGES_LIST_QUERY_KEY = [...ADMIN_MESSAGES_QUERY_KEY, "list"];
export const ADMIN_MESSAGES_DETAIL_QUERY_KEY = [...ADMIN_MESSAGES_QUERY_KEY, "detail"];

const messageDetailKey = (id) => [...ADMIN_MESSAGES_DETAIL_QUERY_KEY, { id: String(id) }];

/**
 * One page of messages for the given API params (see adminMessagesList).
 * `data.results` is the page, `data.counts` the per-status totals for the
 * same filters minus status/bucket (used by the bucket tab badges).
 */
export const useAdminMessages = (params = {}, { enabled = true } = {}) => {
  return useQuery({
    queryKey: [...ADMIN_MESSAGES_LIST_QUERY_KEY, params],
    queryFn: () => adminMessagesList(params),
    enabled,
    refetchOnWindowFocus: false,
    // Keep the previous page visible while a filter / page change refetches.
    placeholderData: keepPreviousData,
  });
};

/** Full detail of one message (body_markdown, metadata, rendered_html for emails). */
export const useAdminMessage = (id, { enabled = true } = {}) => {
  return useQuery({
    queryKey: messageDetailKey(id),
    queryFn: () => adminMessageGet(id),
    enabled: enabled && id != null && id !== "",
    refetchOnWindowFocus: false,
  });
};

/** Cancel a scheduled message; the API answers 409 when it is no longer scheduled. */
export const useCancelAdminMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminMessageCancel(id),
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: ADMIN_MESSAGES_LIST_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: messageDetailKey(id) });
      await queryClient.invalidateQueries({ queryKey: ADMIN_AUTOMATIONS_LIST_QUERY_KEY });
    },
  });
};
